import {NavigationService} from '../services/NavigationService';
import {StyleSettingsService} from '../services/StyleSettingsService';
import {
  CheckoutNavigationService,
  IEcomPlatformPublicApi,
  ModalType,
  SiteStore,
} from '@wix/wixstores-client-storefront-sdk';
import {CartService} from '../services/CartService';
import {EcommerceSettingsService} from '../services/EcommerceSettingsService';
import {CreateCheckoutExceptions, FedopsInteractions} from '../../common/constants';
import {clickOnCheckoutParams} from '@wix/bi-logger-ecom-platform-data/v2/types';
import {OrderService} from '../services/OrderService';
import {MinimumOrderAmountService} from '../services/MinimumOrderAmountService';
import {getAdditionalFeesPrice, getCatalogAppIds, getNumberOfAdditionalFees, getValidations} from '../utils/bi.utils';
import {openNoOnlinePaymentsModal} from '../utils/openNoOnlinePaymentsModal';
import {ICartControllerApi} from '../../types/app.types';
import {SPECS} from '../specs';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {BIService} from '../services/BIService';

export class NavigationStore {
  private readonly navigationService: NavigationService;
  private readonly styleSettingsService: StyleSettingsService;
  private readonly checkoutNavigationService: CheckoutNavigationService;
  private readonly cartService: CartService;
  private readonly orderService: OrderService;
  private readonly ecommerceSettingsService: EcommerceSettingsService;
  private readonly minimumOrderAmountService: MinimumOrderAmountService;
  private readonly controllerApi: ICartControllerApi;
  private readonly origin: string;
  private readonly biService: BIService;
  constructor(
    controllerApi: ICartControllerApi,
    private readonly siteStore: SiteStore,
    private readonly viewerContext: ViewerContext,
    {
      navigationService,
      styleSettingsService,
      checkoutNavigationService,
      cartService,
      ecommerceSettingsService,
      orderService,
      minimumOrderAmountService,
      biService,
    }: {
      navigationService: NavigationService;
      styleSettingsService: StyleSettingsService;
      checkoutNavigationService: CheckoutNavigationService;
      cartService: CartService;
      ecommerceSettingsService: EcommerceSettingsService;
      orderService: OrderService;
      minimumOrderAmountService: MinimumOrderAmountService;
      biService: BIService;
    },
    {origin}: {origin: string}
  ) {
    this.navigationService = navigationService;
    this.styleSettingsService = styleSettingsService;
    this.cartService = cartService;
    this.orderService = orderService;
    this.checkoutNavigationService = checkoutNavigationService;
    this.ecommerceSettingsService = ecommerceSettingsService;
    this.minimumOrderAmountService = minimumOrderAmountService;
    this.controllerApi = controllerApi;
    this.origin = origin;
    this.biService = biService;
  }

  private async handleCheckoutNotAllowed({modalType}: {modalType: ModalType}): Promise<boolean> {
    const cartModel = this.cartService.cartModel;
    const estimatedTotals = this.cartService.estimatedTotals;
    const destination = cartModel.contactInfo?.address;
    const catalogAppId = getCatalogAppIds(cartModel);
    const additionalFeesPrice = getAdditionalFeesPrice(estimatedTotals);
    const additionalFeesNumber = getNumberOfAdditionalFees(estimatedTotals);
    const response = await this.checkoutNavigationService.openLegacyCartModal(
      modalType,
      {destination, catalogAppId, additionalFeesPrice, additionalFeesNumber},
      this.styleSettingsService.isEditorX
    );

    return modalType === ModalType.UpgradeToPremium && response?.proceed;
  }

  private readonly sendClickOnCheckoutBI = async ({
    activePaymentMethods,
  }: {
    activePaymentMethods: {
      name: string;
      type: string;
    }[];
  }) => {
    const BIData: clickOnCheckoutParams = {
      origin: this.origin,
      cartId: this.cartService.cartModel.id,
      itemsCount: this.cartService.itemsCount,
      cartType: this.cartService.cartType,
      productsList: JSON.stringify(
        this.cartService.cartModel.lineItems.map((lineItem) => ({
          id: lineItem.catalogReference.catalogItemId,
          quantity: lineItem.quantity,
        }))
      ),
      ...(this.viewerContext.isExperimentEnabled(SPECS.StopUsingPaymentMethodsInCart)
        ? {}
        : {
            num_of_paymet_providers: activePaymentMethods.length,
            is_with_ewallet_payment: await this.ecommerceSettingsService.hasEWalletPaymentMethods(),
            is_with_offline_payment: await this.ecommerceSettingsService.hasOfflinePaymentMethods(),
            paymet_providers: (await this.ecommerceSettingsService.getPaymentMethodsNames()).join(','),
          }),
      additionalFeesPrice: getAdditionalFeesPrice(this.cartService.estimatedTotals),
      numberOfAdditionalFees: getNumberOfAdditionalFees(this.cartService.estimatedTotals),
      checkoutId: this.cartService.cartModel.checkoutId,
      purchaseFlowId: this.cartService.cartModel.purchaseFlowId,
      catalogAppId: getCatalogAppIds(this.cartService.cartModel),
      validationMessage: getValidations(this.cartService.estimatedTotals?.violations),
    };

    const moveCartToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext);
    if (moveCartToViewerContext) {
      void this.biService.clickOnCheckout(BIData);
    } else {
      void this.siteStore.platformBiLogger.clickOnCheckout({
        ...BIData,
        is_member: !!this.siteStore.usersApi.currentUser.loggedIn,
      });
    }
  };

  private async createCheckoutAndGetIsPreselectedFlow({skipCreateCheckout}: {skipCreateCheckout: boolean}): Promise<{
    createCheckoutResult: string | {error: string};
    isPreselectedFlow?: boolean;
  }> {
    const [isPreselectedFlow, createCheckoutResult] = await Promise.all([
      this.isPreselectedFlow(),
      skipCreateCheckout ? undefined : this.cartService.createCheckout(),
    ]);

    return {createCheckoutResult, isPreselectedFlow};
  }

  private isPreselectedFlow() {
    const {shouldShowShipping} = this.styleSettingsService;
    const shouldCallEstimateTotals = !shouldShowShipping && !this.cartService.isNonShippableCart;
    return shouldCallEstimateTotals
      ? this.cartService.isPreselectedNonShippingFlow()
      : Promise.resolve(this.orderService.isPickup() || this.orderService.hasTimeSlots());
  }

  private async handleCreateCheckoutError(createCheckoutResult: {error: string}) {
    if (createCheckoutResult?.error === CreateCheckoutExceptions.siteMustAcceptPayments) {
      await openNoOnlinePaymentsModal(
        this.checkoutNavigationService,
        this.styleSettingsService.isEditorX,
        this.cartService.cartModel,
        this.cartService.estimatedTotals
      );
    } else {
      await this.cartService.handleGeneralError({
        name: createCheckoutResult.error,
        message: createCheckoutResult.error,
      });
    }
  }
  private readonly preloadCheckoutPage = async () => {
    if (this.viewerContext.isExperimentEnabled(SPECS.PreloadOnCartCheckoutButton)) {
      const api: IEcomPlatformPublicApi = await this.viewerContext.getEcomPlatformPublicAPI();
      await api.checkout.preloadCheckoutPage();
    }
  };

  private readonly handleCheckoutButtonHover = () => {
    void this.preloadCheckoutPage();
  };

  private readonly handleCheckoutButtonClick = async () => {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    await this.controllerApi.reportFedops(FedopsInteractions.CheckoutButtonClicked, async () => {
      this.navigationService.isNavigationToCheckoutInProcess = true;

      const skipCreateCheckout = this.cartService.cartModel.managedByV2;
      let isPreselectedFlow = false;
      const usePickupFlowInsteadOfPreselectedFlow =
        this.viewerContext.isExperimentEnabled(SPECS.CallCartV2FromCheckout) && this.cartService.cartModel.managedByV2;

      if (!usePickupFlowInsteadOfPreselectedFlow) {
        const response = await this.createCheckoutAndGetIsPreselectedFlow({
          skipCreateCheckout,
        });

        if (typeof response.createCheckoutResult !== 'string' && response.createCheckoutResult?.error) {
          await this.handleCreateCheckoutError(response.createCheckoutResult);
          this.navigationService.isNavigationToCheckoutInProcess = false;
          return;
        }

        isPreselectedFlow = response.isPreselectedFlow;
      }

      const {activePaymentMethods, canCheckout, modalType} = await this.canCheckout();

      if (!canCheckout) {
        const shouldProceed = await this.handleCheckoutNotAllowed({modalType});
        if (!shouldProceed) {
          this.navigationService.isNavigationToCheckoutInProcess = false;
          return;
        }
      }

      await this.sendClickOnCheckoutBI({
        activePaymentMethods,
      });

      if (!this.cartService.checkoutId && !skipCreateCheckout) {
        this.navigationService.isNavigationToCheckoutInProcess = false;
        return this.cartService.handleGeneralError({
          name: CreateCheckoutExceptions.noCheckoutId,
          message: CreateCheckoutExceptions.noCheckoutId,
        });
      }

      await this.checkoutNavigationService.navigateToCheckout({
        ...(usePickupFlowInsteadOfPreselectedFlow ? {isPickupFlow: this.orderService.isPickup()} : {isPreselectedFlow}),
        checkoutId: skipCreateCheckout ? this.cartService.cartModel.id : this.cartService.checkoutId,
      });
      this.navigationService.isNavigationToCheckoutInProcess = false;
    });
  };

  private async canCheckout() {
    let activePaymentMethods;
    if (!this.viewerContext.isExperimentEnabled(SPECS.StopUsingPaymentMethodsInCart)) {
      const storeMetaData = await this.ecommerceSettingsService.get();
      activePaymentMethods = storeMetaData.activePaymentMethods;
    }

    const ecommerceSettings = this.ecommerceSettingsService.getEcommerceSettings();

    const checkIsAllowedToCheckoutParams = {
      hasShippableItems: this.cartService.hasShippableItems,
      isPremium: ecommerceSettings.isPremium,
      canStoreShip: ecommerceSettings.delivery?.canBusinessShip,
      hasCreatedPaymentMethods: this.ecommerceSettingsService.hasCreatedPaymentMethods,
      canShipToDestination:
        this.orderService.canShip ||
        this.cartService.isFullAddressRequired ||
        !this.styleSettingsService.shouldShowShipping,
    };

    const {canCheckout, modalType} = this.checkoutNavigationService.checkIsAllowedToCheckout({
      ...checkIsAllowedToCheckoutParams,
      fullPaymentOffline:
        !!this.cartService.estimatedTotals && this.cartService.estimatedTotals.payNow.total.convertedAmount === 0,
    });
    return {activePaymentMethods, canCheckout, modalType};
  }

  private get isCheckoutButtonDisabled() {
    return (
      this.navigationService.isNavigationToCheckoutInProcess ||
      !this.cartService.areAllItemsInStock ||
      this.minimumOrderAmountService.shouldDisableCheckout ||
      this.cartService.hasErrorViolations
    );
  }

  public async toProps() {
    return {
      isCheckoutButtonDisabled: this.isCheckoutButtonDisabled,
      locale: this.viewerContext.isExperimentEnabled(SPECS.MoveCartToViewerContext) ? undefined : this.siteStore.locale,
      continueShopping: this.navigationService.continueShopping,
      continueShoppingHref: await this.navigationService.getContinueShoppingHref(),
      ...(this.viewerContext.isExperimentEnabled(SPECS.UseLinkFromCartItem) && {
        navigateToProduct: this.navigationService.navigateToProduct,
      }),
      navigateToUrl: this.navigationService.navigateToUrl,
      shouldRenderContinueShopping: this.styleSettingsService.shouldRenderContinueShopping,
      handleCheckoutButtonHover: this.handleCheckoutButtonHover,
      handleCheckoutButtonClick: this.handleCheckoutButtonClick,
      navigateToCart: this.navigationService.navigateToCart,
      closeSideCart: this.navigationService.closeSideCart,
      isNavigationToCheckoutInProcess: this.navigationService.isNavigationToCheckoutInProcess,
    };
  }
}
